import { DeleteButton } from "@/components/button/DeleteButton.tsx";
import { WButton } from "@/components/button/WButton.tsx";
import { WModalButton } from "@/components/button/WToolButton.tsx";
import { WModal } from "@/components/modal/WModal.tsx";
import { WTable, WTableBody } from "@/components/table/WTable.tsx";
import { BaseResizeModal, type NodeResizeInputs } from "@/modules/services/kubernetes/NodeResizeModal.tsx";
import { asGibiBytes } from "@/modules/services/kubernetes/utils.ts";
import type { ServiceCreateInputs } from "@/modules/services/serviceCreateViewModel.ts";
import { Plural, Trans, useLingui } from "@lingui/react/macro";
import { useFieldArray, useFormContext } from "react-hook-form";

export function NodePoolsList({ location }: { location: string }) {
    const { t } = useLingui();
    const form = useFormContext<ServiceCreateInputs>();
    const { control } = form;
    const { fields, remove } = useFieldArray({ control, name: "node_pools" });

    function onDelete(index: number) {
        remove(index);
    }

    function onResize(body: NodeResizeInputs, index: number) {
        const currentPools = form.getValues("node_pools") ?? [];

        const updatedPools = currentPools.map((item, i) => {
            if (i === index) {
                return {
                    ...item,
                    name: body.name,
                    minimum: body.autoScalerMin,
                    maximum: body.autoScalerMax,
                    maxSurge: body.maxSurge,
                    machine: { vcpu: body.size.vcpu, ram: body.size.ram },
                    volume: { size: asGibiBytes(body.size.disks) },
                };
            }
            return item;
        });

        form.setValue("node_pools", updatedPools);
    }

    function onAdd(body: NodeResizeInputs) {
        const currentPools = form.getValues("node_pools") ?? [];
        // append([{ source_port: 1, target_port: 2 }]);
        form.setValue("node_pools", [
            ...currentPools,
            {
                name: body.name,
                minimum: body.autoScalerMin,
                maximum: body.autoScalerMax,
                maxSurge: body.maxSurge,
                machine: { vcpu: body.size.vcpu, ram: body.size.ram },
                volume: { size: asGibiBytes(body.size.disks) },
            },
        ]);
    }

    return (
        <WTable
            afterTable={
                <WModal
                    button={
                        <WButton
                            color="primary"
                            variant="dashed"
                            icon="jp-icon-add"
                            action={undefined}
                            className="w-full"
                        >
                            <Trans>Add node pool</Trans>
                        </WButton>
                    }
                >
                    <BaseResizeModal nodes={fields} location={location} onResize={onAdd} />
                </WModal>
            }
        >
            <thead>
                <tr>
                    <th>
                        <Trans>Node pool</Trans>
                    </th>
                    <th>
                        <Trans>Machine type</Trans>
                    </th>
                    <th>
                        <Trans>Disk</Trans>
                    </th>
                    <th>
                        <Trans>Autoscaler min.</Trans>
                    </th>
                    <th>
                        <Trans>Autoscaler max.</Trans>
                    </th>
                    <th>
                        <Trans>Max. surge</Trans>
                    </th>
                    <th />
                </tr>
            </thead>
            <WTableBody>
                {fields.map((item, index) => (
                    <tr key={item.id}>
                        <td>{item.name}</td>
                        <td>
                            {item.machine.vcpu} <Trans>vCPU</Trans> / {item.machine.ram} <Trans>GB</Trans>
                        </td>
                        <td>
                            {item?.volume?.size ? parseInt(item.volume.size, 10) : 0} <Trans>GB</Trans>
                        </td>
                        <td>
                            <Trans>
                                {item.minimum} <Plural value={item.minimum} one="node" other="nodes" />
                            </Trans>
                        </td>
                        <td>
                            <Trans>
                                {item.maximum} <Plural value={item.maximum} one="node" other="nodes" />
                            </Trans>
                        </td>
                        <td>
                            <Trans>
                                {item.maxSurge} <Plural value={item.maxSurge} one="node" other="nodes" />
                            </Trans>
                        </td>
                        <td className="text-right">
                            <div className="flex gap-0.5em justify-end">
                                <WModal button={<WModalButton inTable label={t`Edit`} icon="jp-icon-edit" />}>
                                    <BaseResizeModal
                                        node={item}
                                        nodes={fields}
                                        location={location}
                                        onResize={(body) => onResize(body, index)}
                                    />
                                </WModal>

                                <DeleteButton inTable action={() => onDelete(index)} />
                            </div>
                        </td>
                    </tr>
                ))}
            </WTableBody>
        </WTable>
    );
}
